import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Facebook, Twitter, Linkedin, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", content: "" });

  const load = () => api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/blog/${slug}/comments`, form);
      toast.success("Comment submitted! It will appear after moderation.");
      setForm({ name: "", email: "", content: "" });
    } catch { toast.error("Could not submit. Try again."); }
  };

  if (post === false) return <div className="container-x px-6 py-32 text-center" data-testid="blog-not-found">Post not found.</div>;
  if (!post) return <div className="container-x px-6 py-32 text-center">Loading…</div>;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareUrl = encodeURIComponent(url);

  return (
    <div data-testid="blog-post-page" className="bg-white min-h-screen">
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.featured_image || undefined}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.featured_image ? [post.featured_image] : undefined,
          datePublished: post.created_at,
          author: { "@type": "Organization", name: "AlikamaFish" },
          publisher: { "@type": "Organization", name: "AlikamaFish" },
        }}
      />
      <article className="container-x px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <Link to="/blog" className="text-sm text-[color:var(--color-primary)] hover:text-[color:var(--color-accent)]" data-testid="back-to-blog">← Back to Blog</Link>
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mt-6 mb-3">{post.category}</div>
          <h1 className="font-display text-5xl text-[color:var(--color-primary)] mb-6">{post.title}</h1>
          {post.featured_image && (
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="prose max-w-none text-[color:var(--color-text-secondary)] leading-relaxed" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div className="flex items-center gap-3 mt-12 pt-6 border-t border-[color:var(--color-border)]">
            <span className="text-xs uppercase tracking-widest text-gray-500">Share:</span>
            <a target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} className="p-2 border border-[color:var(--color-border)] rounded hover:bg-gray-100" data-testid="share-facebook"><Facebook className="w-4 h-4"/></a>
            <a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(post.title)}`} className="p-2 border border-[color:var(--color-border)] rounded hover:bg-gray-100" data-testid="share-twitter"><Twitter className="w-4 h-4"/></a>
            <a target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} className="p-2 border border-[color:var(--color-border)] rounded hover:bg-gray-100" data-testid="share-linkedin"><Linkedin className="w-4 h-4"/></a>
            <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(post.title + " " + url)}`} className="p-2 border border-[color:var(--color-border)] rounded hover:bg-gray-100" data-testid="share-whatsapp"><MessageCircle className="w-4 h-4"/></a>
          </div>

          <div className="mt-14">
            <h3 className="font-display text-2xl text-[color:var(--color-primary)] mb-6">Comments ({post.comments?.length || 0})</h3>
            <div className="space-y-4 mb-8">
              {(post.comments || []).map((c) => (
                <div key={c.id} className="bg-[color:var(--color-bg)] p-4 rounded border border-[color:var(--color-border)]" data-testid={`comment-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-gray-500">{(c.created_at || "").split("T")[0]}</div>
                  </div>
                  <div className="text-sm text-[color:var(--color-text-secondary)] mt-2">{c.content}</div>
                </div>
              ))}
              {(!post.comments || post.comments.length === 0) && (
                <div className="text-sm text-gray-500">Be the first to comment.</div>
              )}
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="comment-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="comment-name" />
                <input required type="email" placeholder="Email (won't be shown)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="comment-email" />
              </div>
              <textarea required rows="3" placeholder="Your comment" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="comment-content" />
              <button type="submit" className="btn-primary" data-testid="comment-submit">Post Comment</button>
            </form>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28">
            <div className="bg-[color:var(--color-bg)] border border-[color:var(--color-border)] rounded-lg p-6">
              <div className="text-xs uppercase tracking-widest text-[color:var(--color-accent)] mb-3">Recent Posts</div>
              <RecentSidebar currentSlug={slug} />
            </div>
          </div>
        </aside>
      </article>
    </div>
  );
}

function RecentSidebar({ currentSlug }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);
  return (
    <ul className="space-y-3">
      {posts.filter(p => p.slug !== currentSlug).slice(0, 5).map(p => (
        <li key={p.id}>
          <Link to={`/blog/${p.slug}`} className="text-sm text-[color:var(--color-primary)] hover:text-[color:var(--color-accent)] block" data-testid={`sidebar-post-${p.slug}`}>
            <div className="font-medium">{p.title}</div>
            <div className="text-xs text-gray-500">{p.category}</div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
